import * as Styles from './FeatureVisitors.styles';
import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import {
  GetCountries, IconPeople, IconGlobe, Pagination, SelectField, Skeleton, WorldMap, Icon404File,
  IconAtencion,
} from 'gatorcomponents';
import { isBRServer } from '@/utils/Validators/validation';
import PropTypes from 'prop-types';
import { Chart } from 'react-chartjs-2';
import { formatHour, formatDate } from '@/utils/Formatters/formatDate';
import { ThemeContext } from 'styled-components';
import {
  format, parseISO,
  subDays, subHours,
} from 'date-fns';

const FeatureVisitors = ({
  visitsOnSiteTitle = 'Visitas no site',
  totalOfAccess = 'Total de acessos',
  maxOfVisitants = 'Máximo de visitantes',
  minOfVisitants = 'Mínimo de visitantes',
  perDay = 'Por um dia',
  perHour = 'Por uma hora',
  uniqueVisitants = 'Visitantes únicos',
  regionsOfAccessTitle = 'title',
  countryTitle = 'Pais/Região',
  trafficTitle = 'Tráfego',
  perPageLabel = 'Itens por página',
  noDataTitle = 'Ainda não há dados para exibir',
  noDataDescription = 'Seu site ainda não possui dados de visitas para exibir.',
  errorStateTitle = 'Falha ao carregar os dados',
  errorStateDescription = 'Não foi possível carregar os dados solicitados. Por favor, aguarde e tente novamente em alguns minutos.',
  selectOptions = {},
  countryData = {},
  requestVisitors,
  resetVisitorsState,
  startTimeEndTime,
  requestStatus,
  uniqueVisitorsData = {
    total: 0,
    maximum: 0,
    minimum: 0,
    all_timeslot: [],
  },
  loading = false,
}) => {
  const selectPeriodOptions = [
    {
      value: 1,
      label: selectOptions.last24hours,
    },
    {
      value: 7,
      label: selectOptions.last7Days,
    },
    {
      value: 30,
      label: selectOptions.last30Days,
    },
  ];

  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [periodSelected, setPeriodSelected] = useState(selectPeriodOptions[1]);

  const getPreviousTime = (date) => {
    const subFunction = periodSelected.value === 1 ? subHours : subDays;
    const parsedDate = parseISO(date);
    const previousDate = subFunction(parsedDate, 1);

    return previousDate;
  };

  const formatTooltipDate = date => format(date, 'dd/MM/yyyy - HH:mm');

  const handleSetPeriodSelected = (e) => {
    setPeriodSelected(e);

    if (e.value !== periodSelected.value) {
      requestVisitors && requestVisitors({ days: e.value });
    }
  };

  const periodLabel = periodSelected.value === 1 ? perHour : perDay;

  const theme = useContext(ThemeContext);

  const countries = GetCountries({
    lang: isBRServer ? 'br' : 'es',
  });

  const getPageAmounts = useCallback(() => {
    if (countryData && countryData.all_requests_countries && countryData.all_requests_countries.length) {
      return Math.ceil(countryData.all_requests_countries.length / itemsPerPage);
    }

    return 0;
  }, [
    itemsPerPage,
    countryData,
  ]);

  const pageOptions = [{
    value: 5,
    label: '5',
  },
  {
    value: 10,
    label: '10',
  },
  {
    value: 50,
    label: '50',
  },
  ];

  const getCountryName = useCallback(({ id }) => countries.find(country => country.id.toLowerCase() === id.toLowerCase()).name, [countries]);

  const getCountryData = useCallback(() => countryData && countryData.top5_requests_countries && countryData.top5_requests_countries.map(data => ({
    id: data.key,
    value: data.requests,
    description: `${getCountryName({ id: data.key })} ${data.requests} visitas`,
  })), [
    countryData,
    getCountryName,
  ]);

  const initSlice = page === 0 ? 0 : page * itemsPerPage;
  const endSlice = page === 0 ? itemsPerPage : (initSlice + itemsPerPage);

  const mountCard = ({ title, subtitle, info }) => (
    <Styles.Card>
      <Styles.CardTitle>{title}</Styles.CardTitle>
      <Styles.CardSubtitle>{subtitle}</Styles.CardSubtitle>
      <Styles.CardInfo>{info}</Styles.CardInfo>
    </Styles.Card>
  );

  const handleChangePerPage = (e) => {
    setPage(0);
    setItemsPerPage(e.value);
  };

  useEffect(() => () => {
    resetVisitorsState && resetVisitorsState();
  }, [resetVisitorsState]);

  if (loading) {
    return (
      <Styles.Wrapper>
        <Skeleton
          height="200px"
          width="100%"
        />

        <Skeleton
          height="200px"
          width="100%"
        />
      </Styles.Wrapper>
    );
  }

  const chartUniqueVisitors = uniqueVisitorsData ? uniqueVisitorsData.all_timeslot.map(item => item.unique_visitors) : [];

  const formatTime = periodSelected.value === 1 ? formatHour : formatDate;
  const chartLabels = uniqueVisitorsData ? uniqueVisitorsData.all_timeslot.map(item => formatTime(item.timeslot)) : [];

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: uniqueVisitants,
        data: chartUniqueVisitors,
        backgroundColor: theme.v2.colors.brand.primaryMedium,
        borderColor: theme.v2.colors.brand.primaryMedium,
        tension: 0.3,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 7,
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          font: {
            family: theme.v2.font.family.primary,
            size: 12,
          },
        },
      },
      y: {
        ticks: {
          font: {
            family: theme.v2.font.family.primary,
            size: 12,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const index = tooltipItems[0].dataIndex;
            const currentTime = uniqueVisitorsData.all_timeslot[index].timeslot;
            const startTime = formatTooltipDate(getPreviousTime(currentTime));
            const endTime = formatTooltipDate(parseISO(currentTime));
            return startTimeEndTime(startTime, endTime);
          },
        },
        bodyFont: {
          family: theme.v2.font.family.primary,
          size: 12,
          weight: 500,
        },
        titleFont: {
          family: theme.v2.font.family.primary,
          size: 12,
          weight: 500,
        },
        usePointStyle: true,
        boxHeight: 9,
      },
    },
    interaction: {
      intersect: false,
    },
  };

  const displayErrorState = requestStatus === 'failure';

  if (displayErrorState) {
    return (
      <Styles.Wrapper>
        <Styles.Container>
          <Styles.VisitsEmptyWrapper>
            <Styles.VisitsEmpty>
              <IconAtencion size="xl" testId="IconAtencion" secondaryColor="problemLight" />
              <Styles.TitleEmpty>{ errorStateTitle }</Styles.TitleEmpty>
              <Styles.DescriptionEmpty>{ errorStateDescription }</Styles.DescriptionEmpty>
            </Styles.VisitsEmpty>
          </Styles.VisitsEmptyWrapper>
        </Styles.Container>
      </Styles.Wrapper>
    );
  }

  const displayEmptyState = uniqueVisitorsData && uniqueVisitorsData.total === 0;

  if (displayEmptyState) {
    return (
      <Styles.Wrapper>
        <Styles.Container>
          <Styles.VisitsEmptyWrapper>
            <Styles.VisitsEmpty>
              <Icon404File
                secondaryColor="primaryLight"
                size="xl"
                testId="Icon404File"
              />
              <Styles.TitleEmpty>{ noDataTitle }</Styles.TitleEmpty>
              <Styles.DescriptionEmpty>{ noDataDescription }</Styles.DescriptionEmpty>
            </Styles.VisitsEmpty>
          </Styles.VisitsEmptyWrapper>
        </Styles.Container>
      </Styles.Wrapper>
    );
  }

  return (
    <Styles.Wrapper>
      <Styles.Container>
        <Styles.VisitsWrapper>
          <Styles.Title>
            <IconPeople size="lg" color="primaryExtraDark" secondaryColor="primaryLight" />
            {visitsOnSiteTitle}
          </Styles.Title>

          <Styles.SelectWrapper>
            <SelectField
              testId="selectPeriod"
              options={selectPeriodOptions}
              value={periodSelected.label}
              onClickSelect={e => handleSetPeriodSelected(e)}
              positionAbsolute
            />
          </Styles.SelectWrapper>

          {uniqueVisitorsData && (
            <Styles.CardsContainer>
              {mountCard({ title: totalOfAccess, subtitle: periodSelected.label, info: uniqueVisitorsData.total })}
              {mountCard({ title: maxOfVisitants, subtitle: periodLabel, info: uniqueVisitorsData.maximum })}
              {mountCard({ title: minOfVisitants, subtitle: periodLabel, info: uniqueVisitorsData.minimum })}
            </Styles.CardsContainer>
          )}

          <Styles.ChartContainer>
            <Chart
              type="line"
              data={chartData}
              options={chartOptions}
            />
          </Styles.ChartContainer>
        </Styles.VisitsWrapper>

        <Styles.Title>
          <IconGlobe />
          {regionsOfAccessTitle}
        </Styles.Title>

        <Styles.MapWrapper>
          <WorldMap countryData={getCountryData()} />
        </Styles.MapWrapper>

        <Styles.ListWrapper data-testid="mapList">
          <Styles.ListTitle>
            <Styles.CountryWrapper>{countryTitle}</Styles.CountryWrapper>
            <Styles.AmountWrapper>{trafficTitle}</Styles.AmountWrapper>
          </Styles.ListTitle>

          {countryData && countryData.all_requests_countries.slice(initSlice, endSlice).map((country) => {
            const countryName = getCountryName({ id: country.key });

            if (!countryName) {
              return;
            }

            return (
              <Styles.ListItem key={country.key}>
                <Styles.CountryWrapper>{countryName}</Styles.CountryWrapper>
                <Styles.AmountWrapper>{country.requests}</Styles.AmountWrapper>
              </Styles.ListItem>
            );
          })}

          <Styles.PaginationWrapper>
            <Pagination
              page={page}
              setPage={setPage}
              startIn={1}
              pagesAmount={getPageAmounts()}
            />

            <Styles.SelectWrapper>
              <Styles.PerPagelLabel>
                {perPageLabel}
              </Styles.PerPagelLabel>

              <SelectField
                onClickSelect={handleChangePerPage}
                options={pageOptions}
                size="sm"
                value={itemsPerPage}
                optionsPosition="top"
              />
            </Styles.SelectWrapper>
          </Styles.PaginationWrapper>
        </Styles.ListWrapper>
      </Styles.Container>
    </Styles.Wrapper>
  );
};

FeatureVisitors.propTypes = {
  visitsOnSiteTitle: PropTypes.string,
  totalOfAccess: PropTypes.string,
  maxOfVisitants: PropTypes.string,
  minOfVisitants: PropTypes.string,
  perDay: PropTypes.string,
  perHour: PropTypes.string,
  selectOptions: PropTypes.object,
  requestVisitors: PropTypes.func,
  resetVisitorsState: PropTypes.func,
  requestStatus: PropTypes.string,
  uniqueVisitants: PropTypes.string,
  startTimeEndTime: PropTypes.func,
  regionsOfAccessTitle: PropTypes.string,
  countryData: PropTypes.object,
  uniqueVisitorsData: PropTypes.object,
  loading: PropTypes.bool,
  countryTitle: PropTypes.string,
  trafficTitle: PropTypes.string,
  perPageLabel: PropTypes.string,
};

export default FeatureVisitors;
